import { forwardRef, type SelectHTMLAttributes, useId } from 'react';
import { cn } from './utils';

export type SelectSize = 'sm' | 'md';
export type SelectVariant = 'default' | 'ghost';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: SelectSize;
  /**
   * Visual variant.
   * - `default`: bordered, padded select — the normal form field.
   * - `ghost`: strips border, background, and padding so the select blends
   *   into its container (e.g. an inline "+ Category" picker). Consumer
   *   owns text size/color via className.
   */
  variant?: SelectVariant;
  /**
   * - `false` (default): no error state.
   * - `true`: apply error styling only.
   * - `string`: apply error styling and render the message under the
   *   select with `role="alert"` and aria-describedby wired.
   */
  error?: boolean | string;
  label?: string;
  helpText?: string;
  options: SelectOption[];
  placeholder?: string;
}

const sizeStyles: Record<SelectVariant, Record<SelectSize, string>> = {
  default: {
    md: 'px-4 py-3 rounded-lg',
    sm: 'px-3 py-2 rounded-md',
  },
  ghost: {
    md: 'p-0',
    sm: 'p-0',
  },
};

const variantBase: Record<SelectVariant, string> = {
  default:
    'w-full border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent text-neutral-800 bg-white',
  ghost:
    'border-0 bg-transparent focus:outline-none focus:ring-0 text-neutral-800 cursor-pointer',
};
const errorStyles = 'border-error-border focus:ring-error-fg';
const disabledStyles = 'bg-neutral-50 text-neutral-500 cursor-not-allowed';

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  function Select(props, ref) {
    const {
      size = 'md',
      variant = 'default',
      error = false,
      label,
      helpText,
      options,
      placeholder,
      className = '',
      id: providedId,
      disabled,
      required,
      ...rest
    } = props;

    const generatedId = useId();
    const id = providedId || generatedId;
    const helpId = `${id}-help`;
    const errorId = `${id}-error`;
    const errorMessage = typeof error === 'string' ? error : undefined;
    const hasError = Boolean(error);
    const describedBy = [
      errorMessage ? errorId : null,
      helpText && !errorMessage ? helpId : null,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

    const selectClassName = cn(
      variantBase[variant],
      sizeStyles[variant][size],
      hasError && variant === 'default' && errorStyles,
      disabled && variant === 'default' && disabledStyles,
      className,
    );

    return (
      <div>
        {label && (
          <label htmlFor={id} className="block text-sm font-medium text-neutral-600 mb-2">
            {label}
            {required && <span className="text-error-fg ml-0.5" aria-hidden="true">*</span>}
          </label>
        )}
        <select
          ref={ref}
          id={id}
          className={selectClassName}
          disabled={disabled}
          required={required}
          aria-invalid={hasError || undefined}
          aria-describedby={describedBy}
          {...rest}
        >
          {placeholder && (
            <option value="">{placeholder}</option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {helpText && !errorMessage && (
          <p id={helpId} className="text-xs text-neutral-500 mt-1">{helpText}</p>
        )}
        {errorMessage && (
          <p id={errorId} className="text-xs text-error-fg mt-1" role="alert">{errorMessage}</p>
        )}
      </div>
    );
  }
);
